import { useEffect } from 'react';

import { useVisualViewportHeight } from './useVisualViewportHeight';

/**
 * Keeps a viewport-sized layout aligned with the visible area while the mobile
 * keyboard is open, and hands back the visual-viewport height to size it with.
 *
 * iOS Safari ignores `interactive-widget=resizes-content` and opens the
 * keyboard in *overlay* mode: the layout viewport stays full height and only
 * the visual viewport shrinks. Safari then scrolls the still-full-height
 * document to reveal the focused field. A column sized to the visual viewport
 * therefore ends up scrolled off its own top — the field the driver is typing
 * into sits clipped under the header with a tall empty gap above the keyboard.
 *
 * So while `active`, the document is not allowed to scroll and any scroll iOS
 * already applied is undone whenever the keyboard resizes the viewport. The
 * caller keeps its own `overflowY: auto` region, which is where genuine
 * overflow goes.
 *
 * Chromium honours `resizes-content` and shrinks the whole layout viewport, so
 * none of this is reachable in a desktop browser or in Playwright — only a real
 * iOS device shows the bug or confirms the fix.
 *
 * @param active Whether to hold the document still. The auth screen passes
 *   `true` (it never scrolls), the cabinet passes "is the keyboard open", since
 *   its pages scroll normally the rest of the time.
 */
export const usePinnedToVisualViewport = (active: boolean): number | undefined => {
  const viewportHeight = useVisualViewportHeight();

  useEffect(() => {
    if (!active) return;

    const { body } = document;
    const html = document.documentElement;
    const prevBody = body.style.overflow;
    const prevHtml = html.style.overflow;
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';

    return () => {
      body.style.overflow = prevBody;
      html.style.overflow = prevHtml;
    };
  }, [active]);

  // The keyboard opening or closing is exactly when iOS has just scrolled the
  // document out from under us, so this runs on every viewport height change
  // rather than once.
  useEffect(() => {
    if (!active) return;
    window.scrollTo(0, 0);
  }, [active, viewportHeight]);

  return viewportHeight;
};
